import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAccess } from "@/lib/access";
import {
  Warning,
  WarningOctagon,
  Clock,
  Cube,
  CurrencyInr,
  Factory,
  ShoppingCart,
  Truck,
  CheckCircle,
  ArrowRight,
  ArrowClockwise,
  Plus,
  Package,
  ClipboardText,
  Hourglass,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import IsoReadinessWidget from "@/components/inventory/IsoReadinessWidget";
import StoreQueueWidget from "@/components/inventory/StoreQueueWidget";

/*
 * Layout follows the order people act in: what needs me → headline numbers →
 * my queue → detail. Colour is neutral by default; rose / amber / emerald are
 * reserved for state and always ship with an icon and a text label.
 */

const card = "bg-white border border-slate-200 rounded-lg";

const fmtINR = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// ₹12,34,567 → ₹12.3L ; keeps headline tiles short. Full value goes in the title.
function fmtINRCompact(n) {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return fmtINR(v);
}

function daysFromToday(iso) {
  if (!iso) return null;
  const today = new Date(new Date().toISOString().slice(0, 10));
  return Math.round((new Date(iso) - today) / 86400000);
}

const SEVERITY = {
  critical: { dot: "bg-rose-500", text: "text-rose-700", ring: "border-rose-200 hover:border-rose-300", icon: WarningOctagon, word: "Critical" },
  warning: { dot: "bg-amber-500", text: "text-amber-700", ring: "border-amber-200 hover:border-amber-300", icon: Warning, word: "Warning" },
  info: { dot: "bg-sky-500", text: "text-sky-700", ring: "border-slate-200 hover:border-slate-300", icon: Hourglass, word: "Pending" },
};

export default function InventoryDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadedAt, setLoadedAt] = useState(null);
  const [stockTab, setStockTab] = useState("out");
  const stockRef = useRef(null);
  const { can } = useAccess();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/dashboard");
      setData(data);
      setLoadedAt(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return <DashboardSkeleton />;
  }
  if (!data) {
    return (
      <div className="p-6">
        <div className={`${card} p-6 text-sm text-rose-700 flex items-center justify-between`}>
          <span className="inline-flex items-center gap-2">
            <WarningOctagon size={16} weight="fill" /> Couldn&apos;t load the dashboard.
          </span>
          <button onClick={load} className="text-xs font-medium text-slate-700 hover:text-slate-900">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const po = data.purchase_orders || {};
  const pt = data.production_today || {};

  const openStockTab = (tab) => {
    setStockTab(tab);
    stockRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Exceptions, worst first. Each one says what is wrong in plain words and where to fix it.
  const alerts = [
    { key: "out", severity: "critical", count: data.out_of_stock_count, label: "Out of stock", hint: "Reorder now", onClick: () => openStockTab("out"), testId: "kpi-out-of-stock" },
    { key: "expired", severity: "critical", count: data.expired_count, label: "Expired batches", hint: "Quarantine or dispose", onClick: () => openStockTab("expired"), testId: "kpi-expired" },
    { key: "overdue", severity: "critical", count: po.external_overdue || 0, label: "Overdue deliveries", hint: "Chase the vendor", to: "/inventory/purchase-orders", testId: "kpi-overdue-deliveries" },
    { key: "qc", severity: "warning", count: data.qc_pending_count || 0, label: "Deliveries waiting for QA", hint: "Accept or reject", to: "/inventory/procurement?tab=grns", testId: "kpi-qc-pending" },
    { key: "ipo", severity: "warning", count: (po.internal_pending_approval || 0) + (po.external_pending_approval || 0), label: "POs to approve", hint: "Approve or reject", to: "/inventory/purchase-orders", testId: "kpi-ipo-pending" },
    { key: "low", severity: "warning", count: data.low_stock_count, label: "Below reorder level", hint: "Raise a PO", onClick: () => openStockTab("low"), testId: "kpi-low-stock" },
    { key: "near", severity: "warning", count: data.near_expiry_count, label: "Expiring in 30 days", hint: "Use first (FEFO)", onClick: () => openStockTab("near"), testId: "kpi-near-expiry" },
    { key: "recipes", severity: "info", count: data.recipes_pending, label: "Recipes awaiting review", hint: "Review drafts", to: "/inventory/recipes", testId: "kpi-recipes-pending" },
  ];
  const active = alerts.filter((a) => a.count > 0);
  const clear = alerts.filter((a) => a.count === 0);

  // One primary action per role, at most two secondary ones.
  const actions = [
    can("production.run") && { to: "/inventory/production-wizard", label: "Start production run", icon: Factory, testId: "qa-start-run" },
    can("po.receive") && { to: "/inventory/procurement", label: "Receive goods", icon: Truck, testId: "qa-receive-goods" },
    can("po.create") && { to: "/inventory/purchase-orders", label: "New purchase order", icon: ShoppingCart, testId: "qa-new-po" },
    can("requisition.create") && { to: "/inventory/requisitions", label: "Raise requisition", icon: ClipboardText, testId: "qa-new-requisition" },
  ].filter(Boolean).slice(0, 3);

  const coverage = data.items_total ? Math.round((data.items_available / data.items_total) * 100) : 0;

  return (
    <div className="p-6 space-y-6 max-w-[1600px]" data-testid="inv-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">
            Inventory & ERP
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Inventory overview</h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <span>Store, purchasing and production at a glance.</span>
            {loadedAt && (
              <span className="text-slate-400" data-testid="dashboard-updated-at">
                · Updated {loadedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
              data-testid="dashboard-refresh"
              aria-label="Refresh dashboard"
            >
              <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
        {actions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-actions">
            {actions.map((a, i) => (
              <Link
                key={a.to + a.label}
                to={a.to}
                data-testid={a.testId}
                className={
                  "inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm font-medium transition " +
                  (i === 0
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50")
                }
              >
                {i === 0 ? <Plus size={16} weight="bold" /> : <a.icon size={16} weight="duotone" />}
                {a.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Needs attention */}
      <section className={card} data-testid="needs-attention">
        <div className="px-5 pt-4 pb-3 flex items-center justify-between">
          <h2 className="font-heading text-base font-semibold text-slate-900">Needs attention</h2>
          <span className="text-xs text-slate-500">
            {active.length === 0 ? "Nothing waiting" : `${active.length} of ${alerts.length} checks need action`}
          </span>
        </div>
        {active.length === 0 ? (
          <div className="mx-5 mb-4 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-2 text-sm text-emerald-800" data-testid="kpi-stock-health">
            <CheckCircle size={18} weight="fill" /> All clear. Stock, expiry, deliveries and approvals are on track.
          </div>
        ) : (
          <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {active.map((a) => (
              <AlertTile key={a.key} alert={a} />
            ))}
          </div>
        )}
        {active.length > 0 && clear.length > 0 && (
          <div className="px-5 py-2.5 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="font-medium text-slate-600">On track:</span>
            {clear.map((a) => (
              <span key={a.key} className="inline-flex items-center gap-1" data-testid={a.testId}>
                <CheckCircle size={12} weight="fill" className="text-emerald-500" /> {a.label}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Headline numbers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi
          label="Inventory value"
          value={fmtINRCompact(data.inventory_value)}
          title={fmtINR(data.inventory_value)}
          sub="Available stock at cost"
          icon={CurrencyInr}
          testId="kpi-inventory-value"
        />
        <Kpi
          label="Items in stock"
          value={`${data.items_available} / ${data.items_total}`}
          sub={`${coverage}% of active items have stock`}
          icon={Cube}
          to="/inventory/items"
          testId="kpi-items-available"
          meter={coverage}
        />
        <Kpi
          label="Production today"
          value={`${pt.completed_count || 0} / ${pt.planned_count || 0}`}
          sub={`runs done · ${pt.in_progress_count || 0} in progress`}
          icon={Factory}
          to="/inventory/production-runs"
          testId="kpi-production-today"
        />
        <Kpi
          label="Open purchase orders"
          value={po.external_open || 0}
          sub={
            po.external_overdue ? (
              <span className="text-rose-700 font-medium inline-flex items-center gap-1">
                <WarningOctagon size={12} weight="fill" /> {po.external_overdue} overdue
              </span>
            ) : (
              "None overdue"
            )
          }
          icon={ShoppingCart}
          to="/inventory/purchase-orders"
          testId="kpi-open-pos"
        />
      </div>

      {/* Store approval queue (shown only to Store/admin roles) */}
      <StoreQueueWidget />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2 scroll-mt-6" ref={stockRef}>
          <StockAlerts data={data} tab={stockTab} onTab={setStockTab} />
        </div>
        <PurchaseOrdersWidget data={po} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2">
          <ProductionToday data={pt} />
        </div>
        <CostHeatmap data={data.cost_heatmap} />
      </div>

      {/* ISO 22000 readiness changes slowly, so it sits below the daily work */}
      <IsoReadinessWidget />
    </div>
  );
}

function DashboardSkeleton() {
  const bar = "bg-slate-200/70 rounded animate-pulse";
  return (
    <div className="p-6 space-y-6" data-testid="inv-dashboard-loading" aria-busy="true">
      <div className={`${bar} h-8 w-64`} />
      <div className={`${bar} h-28 w-full`} />
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`${bar} h-24`} />
        ))}
      </div>
      <div className={`${bar} h-64 w-full`} />
      <span className="sr-only">Loading inventory…</span>
    </div>
  );
}

function AlertTile({ alert }) {
  const s = SEVERITY[alert.severity];
  const Icon = s.icon;
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-semibold ${s.text}`}>
          <Icon size={12} weight="fill" /> {s.word}
        </span>
        <ArrowRight size={14} weight="bold" className="text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition" />
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-heading text-2xl font-bold text-slate-900 tabular-nums leading-none">{alert.count}</span>
        <span className="text-sm font-medium text-slate-700">{alert.label}</span>
      </div>
      <div className="text-xs text-slate-500 mt-1">{alert.hint}</div>
    </>
  );
  const cls = `group block text-left w-full border ${s.ring} rounded-md px-4 py-3 bg-white hover:shadow-sm transition`;
  return alert.to ? (
    <Link to={alert.to} className={cls} data-testid={alert.testId}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={alert.onClick} className={cls} data-testid={alert.testId}>
      {body}
    </button>
  );
}

function Kpi({ label, value, title, sub, icon: Icon, to, testId, meter }) {
  const inner = (
    <div className={`${card} p-5 h-full ${to ? "hover:shadow-sm hover:border-slate-300 transition" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
        <Icon size={18} weight="duotone" className="text-slate-400" />
      </div>
      <div className="font-heading text-3xl font-bold text-slate-900 mt-2 leading-none tabular-nums" title={title}>
        {value}
      </div>
      {meter !== undefined && (
        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden" aria-hidden>
          <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${Math.min(100, meter)}%` }} />
        </div>
      )}
      {sub && <div className="text-xs text-slate-500 mt-2">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

function CardHeader({ title, subtitle, to, linkLabel, linkTestId, right }) {
  return (
    <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="font-heading text-base font-semibold text-slate-900">{title}</h2>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {right}
      {to && (
        <Link
          to={to}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
          data-testid={linkTestId}
        >
          {linkLabel} <ArrowRight size={11} weight="bold" />
        </Link>
      )}
    </div>
  );
}

const STOCK_TABS = [
  { key: "out", label: "Out of stock", countKey: "out_of_stock_count", rowsKey: "out_of_stock", testId: "panel-out-of-stock", empty: "Nothing out of stock." },
  { key: "low", label: "Low stock", countKey: "low_stock_count", rowsKey: "low_stock", testId: "panel-low-stock", empty: "Everything is above its reorder level." },
  { key: "near", label: "Expiring soon", countKey: "near_expiry_count", rowsKey: "near_expiry", testId: "panel-near-expiry", empty: "No batches expire in the next 30 days." },
  { key: "expired", label: "Expired", countKey: "expired_count", rowsKey: "expired", testId: "panel-expired", empty: "No expired stock." },
];

function StockAlerts({ data, tab, onTab }) {
  const current = STOCK_TABS.find((t) => t.key === tab) || STOCK_TABS[0];
  const rows = data[current.rowsKey] || [];
  const total = data[current.countKey] || 0;
  const critical = current.key === "out" || current.key === "expired";

  return (
    <section className={card} data-testid="stock-alerts">
      <CardHeader
        title="Stock alerts"
        subtitle="Items and batches that need a reorder or a decision"
        to="/inventory/stock"
        linkLabel="Stock ledger"
        linkTestId="stock-alerts-ledger-link"
      />
      {/* Baseline is an inset shadow, not a border: tabs then don't need -mb-px to sit on it,
          so overflow-x-auto doesn't sprout a vertical scrollbar from 1px of overflow. */}
      <div className="px-5 shadow-[inset_0_-1px_0_0_theme(colors.slate.200)] flex gap-5 overflow-x-auto overflow-y-hidden" role="tablist">
        {STOCK_TABS.map((t) => {
          const n = data[t.countKey] || 0;
          const on = t.key === current.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={on}
              onClick={() => onTab(t.key)}
              data-testid={`stock-tab-${t.key}`}
              className={
                "py-2.5 border-b-2 text-sm font-medium whitespace-nowrap inline-flex items-center gap-2 transition " +
                (on ? "border-emerald-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800")
              }
            >
              {t.label}
              <span
                className={
                  "min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold tabular-nums inline-flex items-center justify-center " +
                  (n === 0 ? "bg-slate-100 text-slate-500" : on ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-700")
                }
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <div role="tabpanel" data-testid={current.testId}>
        {rows.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">
            <CheckCircle size={28} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
            {current.empty}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                {(current.key === "out" || current.key === "low"
                  ? ["Item", "Code", current.key === "low" ? "On hand vs reorder level" : "Available"]
                  : ["Item", "Batch · Location", "Expiry"]
                ).map((h) => (
                  <th key={h} className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold px-5 py-2 text-left last:text-right">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) =>
                current.key === "out" ? (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-5 py-2.5 text-sm font-medium text-slate-900">{r.name}</td>
                    <td className="px-5 py-2.5 text-xs font-mono text-slate-500">{r.code}</td>
                    <td className="px-5 py-2.5 text-sm text-right tabular-nums">
                      <span className="inline-flex items-center gap-1 text-rose-700 font-medium">
                        <WarningOctagon size={12} weight="fill" /> 0 {r.uom || ""}
                      </span>
                    </td>
                  </tr>
                ) : current.key === "low" ? (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-5 py-2.5 text-sm font-medium text-slate-900">{r.name}</td>
                    <td className="px-5 py-2.5 text-xs font-mono text-slate-500">{r.code}</td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center justify-end gap-3">
                        <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden" aria-hidden>
                          <div
                            className="h-full bg-amber-500 rounded-full"
                            style={{ width: `${Math.min(100, (r.qty / (r.reorder_level || 1)) * 100)}%` }}
                          />
                        </div>
                        <span className="text-sm tabular-nums text-slate-700 whitespace-nowrap">
                          {r.qty} / {r.reorder_level} {r.uom || ""}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <ExpiryRow key={r.id} row={r} />
                )
              )}
            </tbody>
          </table>
        )}
        {total > rows.length && (
          <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            Showing {rows.length} of {total}
            {critical ? ", worst first." : ", soonest first."}{" "}
            <Link to="/inventory/stock" className="font-medium text-emerald-700 hover:text-emerald-900">
              See all in Stock Ledger
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function ExpiryRow({ row }) {
  const d = daysFromToday(row.expiry_date);
  const expired = d !== null && d < 0;
  const rel = d === null ? "" : expired ? `${-d}d ago` : d === 0 ? "today" : `in ${d}d`;
  return (
    <tr className="border-t border-slate-100">
      <td className="px-5 py-2.5 text-sm font-medium text-slate-900">{row.item_name || "—"}</td>
      <td className="px-5 py-2.5 text-xs text-slate-500">
        <span className="font-mono">{row.batch_no}</span> · {row.location}
        {row.qty ? <span className="text-slate-400"> · {row.qty} {row.uom || ""}</span> : null}
      </td>
      <td className="px-5 py-2.5 text-right whitespace-nowrap">
        <div className="text-sm tabular-nums text-slate-700">{row.expiry_date}</div>
        <div className={`text-[11px] font-medium inline-flex items-center gap-1 ${expired || d <= 7 ? "text-rose-700" : "text-amber-700"}`}>
          {expired ? <WarningOctagon size={11} weight="fill" /> : <Clock size={11} weight="fill" />} {rel}
        </div>
      </td>
    </tr>
  );
}

const PO_STATUS_TONE = {
  submitted: "bg-amber-50 text-amber-700",
  approved: "bg-sky-50 text-sky-700",
  rejected: "bg-rose-50 text-rose-700",
  ordered: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  open: "bg-sky-50 text-sky-700",
  partial: "bg-amber-50 text-amber-700",
  fulfilled: "bg-emerald-50 text-emerald-700",
};
const PO_STATUS_LABEL = { submitted: "Waiting approval", partial: "Partly received", fulfilled: "Fully received" };

function poLineSummary(lines = []) {
  if (lines.length === 0) return "—";
  const first = `${lines[0].item_name} × ${lines[0].qty} ${lines[0].uom || "kg"}`;
  return lines.length > 1 ? `${first} +${lines.length - 1} more` : first;
}

function PurchaseOrdersWidget({ data }) {
  const [kind, setKind] = useState("external");
  if (!data) return null;
  const today = new Date().toISOString().slice(0, 10);
  const groups = {
    internal: { rows: data.recent_internal || [], date: "delivery_date", who: (r) => r.created_by_name, empty: "No Internal POs yet." },
    external: { rows: data.recent_external || [], date: "expected_delivery", who: (r) => r.vendor_name, empty: "No External POs yet." },
  };
  const g = groups[kind];

  return (
    <section className={card} data-testid="po-widget">
      <CardHeader
        title="Purchasing"
        subtitle="Approvals and deliveries"
        to="/inventory/purchase-orders"
        linkLabel="All POs"
        linkTestId="po-widget-all-link"
      />
      <div className="grid grid-cols-2 gap-px bg-slate-100 border-b border-slate-200">
        <Stat
          label="Waiting approval"
          value={(data.internal_pending_approval || 0) + (data.external_pending_approval || 0)}
          sub={`${data.internal_pending_approval || 0} internal · ${data.external_pending_approval || 0} external`}
          tone={data.internal_pending_approval || data.external_pending_approval ? "amber" : "slate"}
          testId="po-pending"
        />
        <Stat label="Approved, not ordered" value={data.internal_approved_not_ordered} testId="po-approved" />
        <Stat label="External open" value={data.external_open} testId="po-open" />
        <Stat label="Overdue delivery" value={data.external_overdue} tone={data.external_overdue ? "rose" : "slate"} testId="po-overdue" />
      </div>

      <div className="px-5 pt-3 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Recent</div>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5 text-xs" role="tablist">
          {["external", "internal"].map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={kind === k}
              onClick={() => setKind(k)}
              data-testid={`po-recent-${k}`}
              className={`px-2.5 py-1 rounded capitalize font-medium transition ${kind === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>
      <div className="px-5 pb-4 pt-1">
        {g.rows.length === 0 ? (
          <div className="text-xs text-slate-500 py-6 text-center">{g.empty}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {g.rows.map((r) => {
              const due = r[g.date] || "";
              const late = due && due < today && ["open", "partial", "submitted", "approved"].includes(r.status);
              return (
                <li key={r.id} className="py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-mono text-slate-700">{r.code}</span>
                    <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded whitespace-nowrap ${PO_STATUS_TONE[r.status] || "bg-slate-100 text-slate-600"}`}>
                      {PO_STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                  <div className="text-sm text-slate-800 truncate mt-0.5" title={poLineSummary(r.lines)}>
                    {poLineSummary(r.lines)}
                  </div>
                  <div className="flex items-center justify-between text-[11px] mt-0.5">
                    <span className="text-slate-400 truncate">{g.who(r) || ""}</span>
                    <span className={`tabular-nums whitespace-nowrap ${late ? "text-rose-700 font-medium" : "text-slate-500"}`}>
                      {late && <WarningOctagon size={10} weight="fill" className="inline mr-0.5 -mt-0.5" />}
                      {due ? `Due ${due}` : "—"}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, sub, tone = "slate", valueClass, testId }) {
  const toneMap = {
    sky: "text-sky-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
    slate: "text-slate-900",
  };
  return (
    <div className="bg-white px-5 py-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-lg font-bold leading-tight mt-0.5 tabular-nums ${valueClass || toneMap[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ProductionToday({ data }) {
  if (!data) return null;
  const statusTone = {
    planned: "bg-slate-100 text-slate-600",
    reserved: "bg-amber-50 text-amber-700",
    issuing: "bg-sky-50 text-sky-700",
    in_progress: "bg-sky-50 text-sky-700",
  };
  const variance = data.avg_variance_pct || 0;
  const varianceTone = data.completed_count === 0 ? "slate" : variance < -2 ? "rose" : variance < 0 ? "amber" : "emerald";
  const runs = data.in_progress || [];

  return (
    <section className={card} data-testid="production-today">
      <CardHeader
        title="Production today"
        subtitle="Runs on the floor and how today's batches are yielding"
        to="/inventory/production-runs"
        linkLabel="All runs"
        linkTestId="production-today-all-link"
      />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-100 border-b border-slate-200">
        <Stat label="In progress" value={data.in_progress_count} tone="sky" testId="pt-in-progress" />
        <Stat label="Planned today" value={data.planned_count} sub={`yield ${data.planned_yield_kg} kg`} testId="pt-planned-count" />
        <Stat label="Planned cost" value={fmtINRCompact(data.planned_cost)} sub={fmtINR(data.planned_cost)} testId="pt-planned-cost" />
        <Stat label="Completed" value={data.completed_count} sub={`yield ${data.actual_yield_kg} kg`} tone="emerald" testId="pt-completed-count" />
        <Stat
          label="Avg yield variance"
          value={data.completed_count > 0 ? `${variance > 0 ? "+" : ""}${variance}%` : "—"}
          tone={varianceTone}
          sub={data.completed_count > 0 ? (variance < -2 ? "Below plan" : variance < 0 ? "Slightly below" : "On or above plan") : "No runs completed"}
          testId="pt-variance"
        />
      </div>

      {runs.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <Factory size={28} weight="duotone" className="mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-700 font-medium">No runs in progress.</div>
          <div className="text-xs text-slate-500 mt-1">
            <Link to="/inventory/production-wizard" className="text-emerald-700 font-medium hover:underline">
              Start a production run
            </Link>{" "}
            to put materials in motion.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                {["Run", "Batch", "Status", "Material cost", ""].map((h, i) => (
                  <th key={h || i} className="text-left px-5 py-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pt-row-${r.id}`}>
                  <td className="px-5 py-3">
                    <div className="text-sm font-medium text-slate-900">{r.recipe_name}</div>
                    <div className="text-[11px] font-mono text-slate-500">
                      {r.code} · {r.materials_consumed} batches issued
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm tabular-nums text-slate-700">{r.batch_size} kg</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${statusTone[r.status] || "bg-slate-100 text-slate-600"}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-600 tabular-nums">
                    {r.actual_material_cost ? (
                      fmtINR(r.actual_material_cost)
                    ) : (
                      <span className="text-slate-400">planned {fmtINR(r.total_material_cost)}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link
                      to={`/inventory/production-runs/${r.id}`}
                      className="text-xs font-medium text-emerald-700 hover:text-emerald-900"
                      data-testid={`pt-open-${r.id}`}
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Single series → one hue; bar length carries magnitude, the % is printed beside it.
function CostHeatmap({ data }) {
  if (!data) return null;
  const rows = data.rows || [];
  const maxCost = rows.length > 0 ? Math.max(...rows.map((r) => r.total_cost)) : 0;

  return (
    <section className={card} data-testid="cost-heatmap">
      <CardHeader
        title="Material spend today"
        subtitle="Top 10 ingredients consumed by production"
        right={
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Total</div>
            <div className="text-base font-bold text-slate-900 tabular-nums" data-testid="heatmap-total">
              {fmtINR(data.total_cost_today)}
            </div>
          </div>
        }
      />
      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <Package size={28} weight="duotone" className="mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-700 font-medium">No material consumed yet today.</div>
          <div className="text-xs text-slate-500 mt-1">Spend appears here once a production run issues stock.</div>
        </div>
      ) : (
        <ol className="px-5 py-4 space-y-3">
          {rows.map((r, idx) => (
            <li
              key={r.item_id}
              data-testid={`heatmap-row-${r.item_id}`}
              title={`${r.item_name}: ${fmtINR(r.total_cost)} · ${r.qty_consumed} ${r.uom} over ${r.batches} runs · ${r.share_pct}% of top-10 spend`}
            >
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">
                  <span className="text-[10px] font-mono text-slate-400 mr-1.5">{String(idx + 1).padStart(2, "0")}</span>
                  <span className="font-medium text-slate-900">{r.item_name}</span>
                </span>
                <span className="tabular-nums text-slate-900 font-semibold whitespace-nowrap">{fmtINR(r.total_cost)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${idx === 0 ? "bg-emerald-700" : "bg-emerald-500"}`}
                    style={{ width: `${maxCost > 0 ? Math.max(3, (r.total_cost / maxCost) * 100) : 3}%` }}
                  />
                </div>
                <span className="w-10 text-right text-[11px] tabular-nums text-slate-500">{r.share_pct}%</span>
              </div>
            </li>
          ))}
        </ol>
      )}
      {rows.length > 0 && (
        <div className="px-5 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500">
          % = share of top-10 spend · today (UTC)
        </div>
      )}
    </section>
  );
}
